import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Play, CheckCircle2, Pencil, Plus, BarChart3 } from "lucide-react";
import { toast } from "sonner";
import api, { apiError } from "../lib/api";
import { useLang } from "../lib/i18n";
import { useAuth } from "../context/AuthContext";
import ProjectTabs, { useWorkSummary } from "../components/work/ProjectTabs";
import QuickFilters, { useProjectFilters } from "../components/work/QuickFilters";
import { TypeIcon, PriorityIcon, Avatar, StatusPill } from "../components/work/workMeta";
import { fmtH, fmtMoney } from "../components/work/WorkLog";
import EpicRollup, { SprintCost } from "../components/work/CostRollup";
import { BoardFilters, applyFilters, filterCtx, useWorkItems } from "./Board";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Skeleton } from "../components/ui/skeleton";

const BACKLOG = "backlog";
const BACKLOG_PARAMS = { include_done: true };

function Row({ item, canDrag, currency }) {
  const navigate = useNavigate();
  const hasHours = item.estimate_hours != null || item.logged_hours > 0;
  return (
    <div data-testid={`backlog-row-${item.key}`} onClick={() => navigate(`/work/${item.key}`)}
      className="flex items-center gap-2 px-2.5 py-1.5 text-sm bg-white border rounded hover:border-[#1E3A8A]/40 cursor-pointer">
      <TypeIcon type={item.type} />
      <span className="text-xs text-muted-foreground w-16 shrink-0">{item.key}</span>
      <span className="flex-1 truncate">{item.title}</span>
      {item.is_blocked && <span className="text-[10px] text-[#991B1B] font-semibold">BLOCKED</span>}
      {item.price != null && <span className="text-[10px] text-[#065F46] bg-[#D1FAE5] rounded px-1 py-0.5 whitespace-nowrap" data-testid={`backlog-price-${item.key}`}>{fmtMoney(item.price, currency)}</span>}
      {hasHours && <span className={`text-[10px] whitespace-nowrap ${item.estimate_hours != null && item.logged_hours > item.estimate_hours ? "text-[#991B1B]" : "text-muted-foreground"}`} data-testid={`backlog-hours-${item.key}`}>{fmtH(item.logged_hours || 0)}{item.estimate_hours != null ? ` / ${fmtH(item.estimate_hours)}` : ""}</span>}
      <StatusPill name={item.status_name} category={item.status_category} />
      <PriorityIcon priority={item.priority} />
      {item.story_points != null && <span className="text-[10px] font-semibold bg-secondary rounded-full px-1.5 py-0.5">{item.story_points}</span>}
      <Avatar name={item.assignee_name} />
    </div>
  );
}

function SprintDialog({ open, onOpenChange, projectId, sprint, onSaved, mode }) {
  const { t } = useLang();
  const [form, setForm] = useState({ name: "", goal: "", start_date: "", end_date: "" });
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (open) setForm({ name: sprint?.name || "", goal: sprint?.goal || "", start_date: sprint?.start_date || "", end_date: sprint?.end_date || "" });
  }, [open, sprint]);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const submit = async () => {
    setBusy(true);
    try {
      const payload = { name: form.name || null, goal: form.goal, start_date: form.start_date || null, end_date: form.end_date || null };
      if (mode === "start") await api.post(`/sprints/${sprint.id}/start`, payload);
      else if (sprint) await api.patch(`/sprints/${sprint.id}`, payload);
      else await api.post(`/projects/${projectId}/sprints`, payload);
      toast.success(t("saved")); onOpenChange(false); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="sprint-dialog">
        <DialogHeader><DialogTitle className="font-heading">{mode === "start" ? t("startSprint") : sprint ? t("editSprint") : t("createSprint")}</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <div className="space-y-1"><Label>{t("sprintName")}</Label><Input data-testid="sprint-name-input" value={form.name} onChange={set("name")} placeholder="Sprint 1" /></div>
          <div className="space-y-1"><Label>{t("sprintGoal")}</Label><Textarea data-testid="sprint-goal-input" rows={2} value={form.goal} onChange={set("goal")} /></div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>{t("startDate")}</Label><Input data-testid="sprint-start-input" type="date" value={form.start_date} onChange={set("start_date")} /></div>
            <div className="space-y-1"><Label>{t("endDate")}</Label><Input data-testid="sprint-end-input" type="date" value={form.end_date} onChange={set("end_date")} /></div>
          </div>
          <Button data-testid="sprint-save-btn" className="w-full bg-[#1E3A8A] hover:bg-[#1c3479]" disabled={busy} onClick={submit}>{busy ? "..." : mode === "start" ? t("startSprint") : t("save")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function CompleteDialog({ open, onOpenChange, sprint, others, onDone }) {
  const { t } = useLang();
  const [moveTo, setMoveTo] = useState("BACKLOG");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try { await api.post(`/sprints/${sprint.id}/complete`, { move_to: moveTo }); toast.success(t("completedSprint")); onOpenChange(false); onDone(); }
    catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
  };
  if (!sprint) return null;
  const done = sprint.stats?.done_count || 0, total = sprint.stats?.item_count || 0;
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" aria-describedby={undefined} data-testid="complete-sprint-dialog">
        <DialogHeader><DialogTitle className="font-heading">{t("completeSprint")} · {sprint.name}</DialogTitle></DialogHeader>
        <div className="space-y-3 text-sm">
          <p className="text-muted-foreground">{done}/{total} {t("items")} {t("done").toLowerCase()} · {sprint.stats?.done_points}/{sprint.stats?.points} {t("points")}</p>
          {total - done > 0 && (
            <div className="space-y-1"><Label>{t("moveUnfinishedTo")} ({total - done})</Label>
              <Select value={moveTo} onValueChange={setMoveTo}>
                <SelectTrigger data-testid="complete-move-select"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="BACKLOG">{t("backlog")}</SelectItem>{others.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}</SelectContent>
              </Select></div>
          )}
          <Button data-testid="complete-sprint-btn" className="w-full bg-[#1E3A8A] hover:bg-[#1c3479]" disabled={busy} onClick={submit}>{busy ? "..." : t("completeSprint")}</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

function Bucket({ id, title, meta, items, canDrag, actions, children, currency }) {
  const { t } = useLang();
  return (
    <section className="apha-card" data-testid={`bucket-${id}`}>
      <div className="px-4 py-2.5 border-b flex flex-wrap items-center gap-2 justify-between">
        <div className="flex items-center gap-2 min-w-0">{title}{meta}</div>
        <div className="flex items-center gap-1.5">{actions}</div>
      </div>
      <Droppable droppableId={id} isDropDisabled={!canDrag}>
        {(provided, snapshot) => (
          <div ref={provided.innerRef} {...provided.droppableProps} className={`p-2 space-y-1.5 min-h-[56px] ${snapshot.isDraggingOver ? "bg-[#DBEAFE]/50" : ""}`}>
            {items.map((item, idx) => (
              <Draggable key={item.id} draggableId={item.id} index={idx} isDragDisabled={!canDrag}>
                {(p) => <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps}><Row item={item} canDrag={canDrag} currency={currency} /></div>}
              </Draggable>
            ))}
            {provided.placeholder}
            {items.length === 0 && <div className="text-xs text-muted-foreground px-2 py-2">{t("noData")}</div>}
          </div>
        )}
      </Droppable>
      {children}
    </section>
  );
}

export default function Backlog() {
  const { projectId } = useParams();
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const { summary, sprints, reload } = useWorkSummary(projectId);
  const [filters, setFilters] = useProjectFilters(projectId);
  const [sprintDlg, setSprintDlg] = useState({ open: false, sprint: null, mode: "edit" });
  const [completeDlg, setCompleteDlg] = useState(null);
  const [quick, setQuick] = useState("");
  const { items, setItems, load, queryError } = useWorkItems(projectId, filters, BACKLOG_PARAMS, (rows) => rows.filter((i) => i.type !== "SUBTASK"));
  const refresh = () => { reload(); load(); };

  const onDragEnd = async (result) => {
    const { destination, source, draggableId } = result;
    if (!destination) return;
    const item = items.find((i) => i.id === draggableId);
    const destList = visible.filter((i) => (destination.droppableId === BACKLOG ? !i.sprint_id : i.sprint_id === destination.droppableId) && i.id !== draggableId);
    const before = destList[destination.index - 1] || null;
    const after = destList[destination.index] || null;
    try {
      if (destination.droppableId !== source.droppableId) {
        await api.post(`/work-items/${item.key}/sprint`, { sprint_id: destination.droppableId === BACKLOG ? null : destination.droppableId });
      }
      await api.post(`/work-items/${item.key}/rank`, { before_id: before?.id || null, after_id: after?.id || null });
      toast.success(`${item.key} ${t("moved").toLowerCase()}`);
    } catch (e) { toast.error(apiError(e)); }
    refresh();
  };

  const quickCreate = async () => {
    if (!quick.trim()) return;
    try {
      const { data } = await api.post(`/projects/${projectId}/work-items`, { type: "STORY", title: quick.trim() });
      toast.success(`${t("itemCreated")} · ${data.key}`); setQuick(""); refresh();
    } catch (e) { toast.error(apiError(e)); }
  };

  if (!summary || !items) return <Skeleton className="h-96 w-full" />;
  if (summary.project.methodology === "KANBAN") {
    return (<div className="space-y-4"><ProjectTabs summary={summary} sprints={sprints} onCreated={refresh} /><div className="apha-card p-6 text-sm text-muted-foreground" data-testid="kanban-no-sprints">{t("kanbanNoSprints")}</div></div>);
  }
  const visible = applyFilters(items, filters, filterCtx(summary, user));
  const canDrag = summary.access !== "READ";
  const canManage = summary.access === "MANAGE";
  const openSprints = sprints.filter((s) => s.status !== "COMPLETED");
  const backlogItems = visible.filter((i) => !i.sprint_id && i.status_category !== "DONE");

  return (
    <div className="space-y-4" data-testid="backlog-page">
      <ProjectTabs summary={summary} sprints={sprints} onCreated={refresh}>
        {canManage && <Button size="sm" variant="outline" data-testid="create-sprint-btn" onClick={() => setSprintDlg({ open: true, sprint: null, mode: "edit" })}><Plus className="h-4 w-4 mr-1" />{t("createSprint")}</Button>}
      </ProjectTabs>
      <QuickFilters projectId={projectId} summary={summary} filters={filters} setFilters={setFilters} />
      <BoardFilters summary={summary} filters={filters} setFilters={setFilters} queryError={queryError} count={items.length} />
      <p className="text-xs text-muted-foreground">{t("dragHint")}</p>
      <EpicRollup projectId={projectId} refreshKey={items} />
      <DragDropContext onDragEnd={onDragEnd}>
        <div className="space-y-4">
          {openSprints.map((s) => {
            const sItems = visible.filter((i) => i.sprint_id === s.id);
            return (
              <Bucket key={s.id} id={s.id} items={sItems} canDrag={canDrag} currency={summary.project.currency}
                title={<><span className="font-heading font-semibold">{s.name}</span>
                  <span className={`text-[10px] font-semibold border rounded px-1.5 py-0.5 ${s.status === "ACTIVE" ? "text-[#166534] bg-[#DCFCE7] border-[#BBF7D0]" : "text-[#334155] bg-[#F1F5F9] border-[#E2E8F0]"}`} data-testid={`sprint-status-${s.id}`}>{s.status === "ACTIVE" ? t("activeSprint") : t("plannedSprint")}</span></>}
                meta={<><span className="text-xs text-muted-foreground truncate">{s.start_date && s.end_date ? `${s.start_date} → ${s.end_date} · ` : ""}{s.stats.item_count} {t("items")} · {s.stats.done_points}/{s.stats.points} {t("points")}{s.goal ? ` · “${s.goal}”` : ""}</span><SprintCost stats={s.stats} currency={summary.project.currency} id={s.id} /></>}
                actions={<>
                  <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`sprint-report-${s.id}`} onClick={() => navigate(`/sprints/${s.id}`)}><BarChart3 className="h-3.5 w-3.5 mr-1" />{t("sprintReport")}</Button>
                  {canManage && <Button size="sm" variant="ghost" className="h-7 text-xs" data-testid={`sprint-edit-${s.id}`} onClick={() => setSprintDlg({ open: true, sprint: s, mode: "edit" })}><Pencil className="h-3.5 w-3.5" /></Button>}
                  {canManage && s.status === "PLANNED" && <Button size="sm" className="h-7 text-xs bg-[#1E3A8A] hover:bg-[#1c3479]" data-testid={`sprint-start-${s.id}`} onClick={() => setSprintDlg({ open: true, sprint: s, mode: "start" })}><Play className="h-3.5 w-3.5 mr-1" />{t("startSprint")}</Button>}
                  {canManage && s.status === "ACTIVE" && <Button size="sm" variant="outline" className="h-7 text-xs" data-testid={`sprint-complete-${s.id}`} onClick={() => setCompleteDlg(s)}><CheckCircle2 className="h-3.5 w-3.5 mr-1" />{t("completeSprint")}</Button>}
                </>} />
            );
          })}
          <Bucket id={BACKLOG} items={backlogItems} canDrag={canDrag} currency={summary.project.currency}
            title={<span className="font-heading font-semibold">{t("backlog")}</span>}
            meta={<><span className="text-xs text-muted-foreground">{backlogItems.length} {t("items")} · {backlogItems.reduce((a, i) => a + (i.story_points || 0), 0)} {t("points")}</span>
              <SprintCost id="backlog" showDelivered={false} currency={summary.project.currency} stats={{
                estimate_hours: backlogItems.reduce((a, i) => a + (i.estimate_hours || 0), 0), logged_hours: backlogItems.reduce((a, i) => a + (i.logged_hours || 0), 0),
                remaining_hours: backlogItems.reduce((a, i) => a + (i.remaining_hours || 0), 0),
                price: summary.can_see_price ? backlogItems.reduce((a, i) => a + (i.price || 0), 0) : null, done_price: 0,
                priced_count: backlogItems.filter((i) => i.price != null).length }} /></>}>
            {canDrag && (
              <div className="p-2 border-t">
                <Input data-testid="backlog-quick-create" className="h-8 text-sm" placeholder={t("quickCreate")} value={quick}
                  onChange={(e) => setQuick(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter") quickCreate(); }} />
              </div>
            )}
          </Bucket>
        </div>
      </DragDropContext>
      <SprintDialog open={sprintDlg.open} onOpenChange={(o) => setSprintDlg({ ...sprintDlg, open: o })} projectId={projectId} sprint={sprintDlg.sprint} mode={sprintDlg.mode} onSaved={refresh} />
      <CompleteDialog open={!!completeDlg} onOpenChange={(o) => !o && setCompleteDlg(null)} sprint={completeDlg} others={openSprints.filter((s) => s.status === "PLANNED")} onDone={refresh} />
    </div>
  );
}
